import type { ReactNode, Ref } from 'react'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import { createPortal } from 'react-dom'

import clsx from 'clsx'
import styled, { css } from 'styled-components'

import { VariationModal } from './enum'

import '@hotmart/cosmos/dist/modal'

export type ModalElement = HTMLDivElement & {
  openModal: () => void
  closeModal: () => void
}

export type Variation = VariationModal

export type ModalProps = {
  children: ReactNode
  show: boolean
  className?: string
  onClose: () => void
  centered?: boolean
  docked?: boolean
  variation?: Variation
}

let modalRoot = document.getElementById('modal')
if (!modalRoot) {
  modalRoot = document.createElement('div')
  modalRoot.setAttribute('id', 'modal')
  document.body.appendChild(modalRoot)
}

const ModalDefault = forwardRef<ModalElement, ModalProps>(
  (
    {
      children,
      show,
      onClose,
      centered = true,
      docked = false,
      variation,
      className = ''
    }: ModalProps,
    ref: Ref<ModalElement>
  ) => {
    const modalRef = useRef<ModalElement>(null)
    useImperativeHandle(ref, () => modalRef.current as ModalElement)

    useEffect(() => {
      if (show) {
        modalRef.current?.openModal()

        return
      }

      modalRef.current?.closeModal()
    }, [show])

    useEffect(() => {
      const current = modalRef.current
      current?.addEventListener('close', onClose)

      return () => current?.removeEventListener('close', onClose)
    }, [onClose])

    const modalClasses = clsx({ 'hot-dropdocked': docked }, className)

    if (!show) return null

    return createPortal(
      <hot-modal
        ref={modalRef}
        variation={variation}
        class={modalClasses}
        {...(centered ? { position: 'centered' } : {})}>
        {children}
      </hot-modal>,
      modalRoot as HTMLElement
    )
  }
)

const Modal = styled(ModalDefault)<ModalProps>`
  ${(props) =>
    props.variation &&
    css`
      --modal-max-width: ${props.variation === VariationModal.BUNDLE ? '87.5rem' : '58.5rem'};
    `}
`

export default Modal
